import { RefObject } from 'react';
import { useMutation } from 'react-query';
import axios from 'axios';
import { UploadProgressRef } from '../hooks/useUploadProgress';
import { authFetch } from '../../../../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../../../../CommonUtils/urlWithParams';
import { useFeatureFlag } from '../../../../../../_hooks/useFeatureFlag';
import { Feature } from '../../../../../../CommonSelectors/CommonArea.types';

interface UseUploadAgreementMutationParams {
  isFirm?: boolean;
  uploadProgressRef: RefObject<UploadProgressRef>;
}

interface UploadFileParams {
  file: File;
}

export const useUploadAgreementMutation = ({
  isFirm,
  uploadProgressRef,
}: UseUploadAgreementMutationParams) => {
  const isRedtailImaging = useFeatureFlag(Feature.RedtailImaging);

  return useMutation(async ({ file }: UploadFileParams) => {
    const controller = new AbortController();

    try {
      const uploadUrl = await authFetch(
        urlWithParams(
          `/Adviser/Settings/${isFirm ? 'FirmAgreementUploadUrl' : 'MyAgreementUploadUrl'}`,
          { fileName: file.name },
        ),
      );

      if (uploadProgressRef?.current) {
        uploadProgressRef.current.bindOnCancel(controller);
      }

      await axios.request({
        method: 'put',
        url: uploadUrl,
        data: file,
        headers: {
          ...(isRedtailImaging ? {} : { 'x-amz-server-side-encryption': 'AES256' }),
          'Content-Type': file.type,
        },
        signal: controller.signal,
        onUploadProgress: p => {
          if (uploadProgressRef?.current) {
            uploadProgressRef.current.onUploadProgress(p);
          }
        },
      });

      if (uploadProgressRef?.current) {
        uploadProgressRef.current.onUploadSuccess();
      }
    } catch (err) {
      if (uploadProgressRef?.current) {
        if (axios.isCancel(err)) {
          uploadProgressRef.current.onCancel();
        } else {
          uploadProgressRef.current.onError();
        }
      }
      throw err;
    }
  });
};
